import React from 'react';
import HojaCaratula from './base/HojaCaratula';

// Etiquetas EXACTAS de las plantillas (templates/caratula/caratula_reajuste_pension_*.docx):
// el modal es una vista previa, así que lo que se lee acá tiene que coincidir renglón por
// renglón con el papel. Cuelga de un trámite GENERICO, así que el bloque del causante y del
// reajuste es override solo para el documento; lo único `guardable` son las claves y el
// domicilio del titular, que van al cliente (ver CaratulaReajustePensionDocumentoService).
//
// CAMPOS es la UNIÓN de las dos variantes; cada layout de abajo nombra solo los suyos. Igual
// que en ModalCaratulaReajuste.js, "DNI/CUIT" y "Montos diferencias"/"MONTO DIFERENCIA"
// aparecen dos veces con etiquetas o dueños distintos (titular vs. causante; Badaro vs.
// Reparación Histórica) — por eso son entradas separadas del mapa.
const CAMPOS = {
    tipoPension: { label: 'REAJUSTAR', tipo: 'texto', nota: 'Valor libre: "DERIVADA", "PENSION DIRECTA", etc., según el reclamo.' },
    ley: { label: 'Ley', tipo: 'texto' },
    numeroBeneficio: { label: 'N° Benef', tipo: 'texto' },
    ingreso: { label: 'INGRESO', tipo: 'fecha' },
    pacto: { label: 'PACTO', tipo: 'sino' },

    claveFiscal: { label: 'CLAVE FISCAL', tipo: 'clave', guardable: true },
    claveCvss: { label: 'CLAVE CVSS', tipo: 'clave', guardable: true },
    referencia: { label: 'REFERENCIA', tipo: 'texto' },

    apellidoNombre: { label: 'APELLIDO/NOMBRE TITULAR', tipo: 'texto' },
    dniCuit: { label: 'DNI/CUIT', tipo: 'texto' },
    telefono: {
        label: 'Nº TELEFONICO', tipo: 'texto',
        nota: 'Trae el teléfono propio y los contactos adicionales del cliente. Se editan desde la ficha del cliente.',
    },
    domicilio: {
        label: 'DOMICILIO', tipo: 'domicilio', estructurado: 'domicilioDniDatos', guardable: true,
        nota: 'Se guarda en el domicilio DNI del cliente (el barrio que ya tenga se conserva). Vacío no borra nada.',
    },

    apellidoNombreCausante: {
        label: 'APELLIDO/NOMBRE CAUSANTE', tipo: 'texto',
        nota: 'El causante no es el cliente del sistema: no hay de dónde traerlo. Solo se usa para este documento.',
    },
    dniCuitCausante: { label: 'DNI/CUIT', tipo: 'texto' },
    fechaNacimientoCausante: { label: 'FECHA DE NACIMIENTO', tipo: 'fecha', noFutura: true },
    fechaFallecimientoCausante: { label: 'FECHA DE FALLECIMIENTO', tipo: 'fecha', noFutura: true },

    expedienteReajustar: { label: 'EXPEDIENTE A REAJUSTAR', tipo: 'texto' },
    turnoVista: { label: 'TURNO PARA TOMAR VISTA', tipo: 'texto' },
    turnoAnses: { label: 'DIA TURNO ANSES RECLAMO ADMINISTRATIVO', tipo: 'fecha' },
    conceptosReclamo: { label: 'Conceptos del reclamo', tipo: 'multilinea' },
    montosDiferencias: { label: 'Montos diferencias', tipo: 'texto', campo: 'montoDiferencia' },
    montoDiferencia: { label: 'MONTO DIFERENCIA', tipo: 'texto' },
    indicePuro: { label: 'Incide puro N°', tipo: 'texto' },
    ipcOtros: { label: 'IPC (Y otros)', tipo: 'texto' },
    observaciones: { label: 'OBSERVACIONES', tipo: 'multilinea' },
};

// Dos carátulas para cualquier reajuste de Pensión: la elige el operador (acá no hay
// tipoReajuste del que derivarla, a diferencia del Reajuste de Jubilación). No son la misma
// carátula con otro título — Badaro suma N° Benef/Conceptos/Índice puro/IPC y no lleva Ley;
// Reparación Histórica suma Ley y tiene un solo renglón de monto.
const LAYOUTS = {
    BADARO: {
        titulo: 'REAJUSTE CASO BADARO',
        cabecera: [['tipoPension', 'numeroBeneficio', 'ingreso', 'pacto']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['apellidoNombreCausante'],
            ['dniCuitCausante'],
            ['fechaNacimientoCausante'],
            ['fechaFallecimientoCausante'],
            ['telefono'],
            ['expedienteReajustar'],
            ['domicilio'],
            ['turnoVista'],
            ['turnoAnses'],
            ['conceptosReclamo'],
            ['montosDiferencias'],
            ['indicePuro'],
            ['ipcOtros'],
            ['observaciones'],
        ],
    },
    REPARACION_HISTORICA: {
        titulo: 'RECLAMO POR AUMENTOS SOBRE REPARACION HISTORICA',
        cabecera: [['tipoPension', 'ley', 'ingreso', 'pacto']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['apellidoNombreCausante'],
            ['dniCuitCausante'],
            ['fechaNacimientoCausante'],
            ['fechaFallecimientoCausante'],
            ['telefono'],
            ['expedienteReajustar'],
            ['domicilio'],
            ['turnoVista'],
            ['montoDiferencia'],
            ['turnoAnses'],
            ['observaciones'],
        ],
    },
};

// CLAVE FISCAL/CVSS y REFERENCIA van igual en las dos variantes y se dibujan aparte, arriba
// del recuadro, tal como están en el papel.
const FILAS_PREVIAS = [['claveFiscal', 'claveCvss'], ['referencia']];

// Ante una variante desconocida se cae a Badaro, igual que el Reajuste de Jubilación.
const hojaDe = (form) => {
    const layout = LAYOUTS[form?.variante] || LAYOUTS.BADARO;
    return {
        tituloDoc: layout.titulo,
        cabecera: layout.cabecera,
        filasPrevias: FILAS_PREVIAS,
        cuerpo: layout.cuerpo,
    };
};

/**
 * Modal de la Carátula de Reajuste de Pensión. Mismo fundamento que ModalCaratulaPension.js
 * (cuelga de un trámite GENERICO, no persiste nada). Dibuja el layout de la variante que
 * corresponda; al confirmar, emite el .docx con el DTO tal cual quedó en el formulario.
 */
export default function ModalCaratulaReajustePension(props) {
    return (
        <HojaCaratula
            {...props}
            titulo="Generar Carátula de Reajuste de Pensión"
            nombreDocumento="la Carátula de Reajuste de Pensión"
            campos={CAMPOS}
            layout={hojaDe}
        />
    );
}
